import { Button } from "@/components/ui/baseUi/button"
import { CalendarInput } from "@/components/ui/forms/calendarInput"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { PhoneNumberInput } from "@/components/ui/forms/phoneNumberInput"
import { SearchUserInput } from "@/components/ui/forms/searchUserInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { TextAreaInput } from "@/components/ui/forms/textareaInput"
import { Label } from "@/components/ui/baseUi/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { cn } from "@/modules/lib/utils"
import type { SimpleFormik } from "@/typings/formik"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxLeadDraftEntity } from "@modules/boxLead/entities/boxLeadInput"
import { ExperienceLevel, Goals, HeardAboutUs } from "@modules/boxLead/entities/boxLeadTypes"
import type { BoxLeadInputKey } from "@modules/boxLead/formik/boxLeadFormik"
import { word } from "@modules/core/utils/i18n"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { displayedDateFormat } from "@modules/utils/dateUtils"
import type { TCountryCode } from "countries-list"
import dayjs from "dayjs"
import { Check, LoaderCircle, X } from "lucide-react"
import { useEffect, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"
import { ChipGroup, ChipGroupItem } from "@/components/ui/baseUi/chip-groupe"

export type BoxLeadFormStepTwoProps = {
  handleSubmit: () => void
  onPrevious: () => void
  formik: SimpleFormik<BoxLeadDraftEntity, BoxLeadInputKey>
  isSubmitting: boolean
  getFormErrorMessage: (name: BoxLeadInputKey, errorMessage?: string) => React.JSX.Element | undefined
  disabled: boolean
  isEdition: boolean
  user?: UserEntity | null
}

export function BoxLeadFormStepTwo({
  handleSubmit,
  onPrevious,
  formik,
  isSubmitting,
  getFormErrorMessage,
  disabled,
  isEdition,
  user,
  className,
  ...props
}: BoxLeadFormStepTwoProps & React.ComponentProps<"div">) {
  const { result: box } = useCurrentBox()
  const selectedVariant = "border-2 border-borderPrimary"
  const neutralVariant = "border border-borderDefault"
  const confirmPopup = useConfirmPopup()

  const [haveHustleUpAccount, setHaveHustleUpAccount] = useState(
    formik.initialValues.userId || !formik.initialValues.firstname ? true : false,
  )

  const hearAboutUsItem = [
    {
      value: HeardAboutUs.WORD_OF_MOUTH,
      label: word("boxLeadEntity.wording.hearAboutUs.wordOfMouth"),
    },
    {
      value: HeardAboutUs.SOCIAL_NETWORK,
      label: word("boxLeadEntity.wording.hearAboutUs.socialNetwork"),
    },
    {
      value: HeardAboutUs.LOCAL_ADVERTISING,
      label: word("boxLeadEntity.wording.hearAboutUs.localAdvertising"),
    },
    {
      value: HeardAboutUs.ONLINE_SEARCH,
      label: word("boxLeadEntity.wording.hearAboutUs.onlineSearch"),
    },
    {
      value: HeardAboutUs.SPORTS_EVENTS_COMPETITIONS,
      label: word("boxLeadEntity.wording.hearAboutUs.events"),
    },
    {
      value: HeardAboutUs.PASSED_BY,
      label: word("boxLeadEntity.wording.hearAboutUs.passedBy"),
    },
    {
      value: HeardAboutUs.INFLUENCERS_AMBASSADORS,
      label: word("boxLeadEntity.wording.hearAboutUs.influencers"),
    },
    {
      value: HeardAboutUs.OTHER,
      label: word("boxLeadEntity.wording.hearAboutUs.other"),
    },
  ]

  const goalsItem = [
    {
      value: Goals.LOSE_WEIGHT,
      label: word("boxLeadEntity.wording.goals.loseWeight"),
    },
    {
      value: Goals.GETTING_BACK_INTO_SPORT,
      label: word("boxLeadEntity.wording.goals.gettingBackIntoSport"),
    },
    {
      value: Goals.RETURN_TO_EXERCISE_AFTER_AN_INJURY,
      label: word("boxLeadEntity.wording.goals.returnToExerciseAfterAnInjury"),
    },
    {
      value: Goals.PERFORMANCE,
      label: word("boxLeadEntity.wording.goals.performance"),
    },
    {
      value: Goals.OTHER,
      label: word("boxLeadEntity.wording.goals.other"),
    },
  ]

  const experienceLevelItem = [
    {
      value: ExperienceLevel.LESS_THAN_SIX_MONTHS,
      label: word("boxLeadEntity.wording.experienceLevel.lessThanSixMonths"),
    },
    {
      value: ExperienceLevel.LESS_THAN_A_YEAR,
      label: word("boxLeadEntity.wording.experienceLevel.lessThanAYear"),
    },
    {
      value: ExperienceLevel.MORE_THAN_A_YEAR,
      label: word("boxLeadEntity.wording.experienceLevel.moreThanAYear"),
    },
    {
      value: ExperienceLevel.NONE,
      label: word("boxLeadEntity.wording.experienceLevel.none"),
    },
  ]

  const bodyEditUserPopup = (
    <div className="flex flex-col content-center items-center justify-center gap-4">
      <Text fontStyle="TL" className="flex text-textDefault-heading">
        {word("box.lead.form.input.popup.edit.header")}
      </Text>
      <Text fontStyle="BM" className="flex w-88 text-center text-textDefault-body">
        {word("box.lead.form.input.popup.edit.content")}
      </Text>
    </div>
  )

  useEffect(() => {
    const countryCode = formik.getFieldProps("phoneNumberCountryCode").value as string | undefined
    if (box?.phoneNumberWithPrefix.countryCode && !countryCode) {
      formik.setFieldValue("phoneNumberCountryCode", box.phoneNumberWithPrefix.countryCode)
    }
  }, [box?.phoneNumberWithPrefix.countryCode])

  const handlePreSubmit = () => {
    if (formik.initialValues.userId && formik.initialValues.userId !== formik.values.userId) {
      confirmPopup.show({
        classNames: {
          dialogContent: "w-104",
          dialogFooter: "flex flex-row justify-around w-full",
          dialogFooterConfirm: "flex-1",
          dialogFooterCancel: "flex-1",
        },
        title: "",
        message: bodyEditUserPopup,
        accept: () => {
          handleSubmit()
        },
        footerProps: {
          cancelText: word("global.cancel"),
          confirmText: word("global.continue"),
        },
      })
      return
    }

    handleSubmit()
  }

  return (
    <div className={cn("mb-4 flex w-full flex-col gap-7", className)} {...props}>
      {/* HAVE HUSTLEUP ACCOUNT */}
      <div className="flex flex-col gap-6 rounded-xl bg-neutral-50 p-6">
        <div className="flex flex-col gap-2">
          <Label fontStyle="LS" className="text-textDefault-body">
            {word("box.lead.form.input.haveHustleUpAccount.label.header").toUpperCase()}
            <Text fontStyle="LL" className="text-textError">
              {" "}
              *
            </Text>
          </Label>
          <div className="flex-rows flex w-full gap-2">
            <div
              className={`flex w-6/12 cursor-pointer items-center justify-center gap-2 rounded-2xl py-6 ${haveHustleUpAccount ? selectedVariant : neutralVariant}`}
              onClick={() => {
                setHaveHustleUpAccount(true)
                formik.setFieldValue("firstname", "")
                formik.setFieldValue("lastname", "")
              }}
            >
              <Check className="text-textDefault-body" size={15} color="currentColor" />
              <Text
                fontStyle="LL"
                className="max-w-36 cursor-pointer text-textDefault-body"
                style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              >
                {word("box.lead.form.input.haveHustleUpAccount.label.yes")}
              </Text>
            </div>
            <div
              className={`flex w-6/12 cursor-pointer items-center justify-center gap-2 rounded-2xl py-6 ${!haveHustleUpAccount ? selectedVariant : neutralVariant}`}
              onClick={() => {
                setHaveHustleUpAccount(false)
                formik.setFieldValue("userId", "")
              }}
            >
              <X className="text-textDefault-body" size={15} color="currentColor" />
              <Text
                fontStyle="LL"
                className="max-w-36 cursor-pointer text-textDefault-body"
                style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              >
                {word("box.lead.form.input.haveHustleUpAccount.label.no")}
              </Text>
            </div>
          </div>
        </div>

        {/* FIRSTNAME / LASTNAME */}
        {!haveHustleUpAccount && (
          <div className="flex-rows flex gap-2">
            <StandardInput
              label={word("global.form.label.firstName")}
              setFieldValue={formik.setFieldValue}
              getFieldProps={formik.getFieldProps}
              name="firstname"
              error={getFormErrorMessage("firstname")}
              inputProps={{
                placeholder: word("auth.signup.form.first_name"),
              }}
              isRequiredInput={true}
            />

            <StandardInput
              label={word("global.form.label.lastName")}
              setFieldValue={formik.setFieldValue}
              getFieldProps={formik.getFieldProps}
              name="lastname"
              error={getFormErrorMessage("lastname")}
              inputProps={{
                placeholder: word("auth.signup.form.last_name"),
              }}
              isRequiredInput={true}
            />
          </div>
        )}

        {/* SEARCH USER */}
        {haveHustleUpAccount && (
          <SearchUserInput
            tooltipContentProps={{ className: "max-w-50", side: "left" }}
            user={user}
            classNameDiv="flex flex-col gap-4"
            textDescription={word("box.lead.form.input.haveHustleUpAccount.label.searchUser")}
            name="userId"
            isRequiredInput={false}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("userId")}
            searchDescriptionText={word("box.lead.form.input.haveHustleUpAccount.dialog.searchUser")}
          />
        )}
      </div>
      {/* FIRST CONTACT DATE */}
      <CalendarInput
        label={word("box.lead.form.input.firstContactDate.label")}
        name="firstContact"
        isRequiredInput
        getFieldProps={formik.getFieldProps}
        error={getFormErrorMessage("firstContact")}
        setFieldValue={formik.setFieldValue}
        placeholder={dayjs().format(displayedDateFormat)}
        calendarProps={{
          minDate: dayjs().subtract(1, "year").toDate(),
          maxDate: dayjs().toDate(),
          dateFormat: displayedDateFormat,
        }}
      />
      {/* EMAIL */}
      <StandardInput
        label={word("global.form.label.email")}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        name="email"
        error={getFormErrorMessage("email")}
        inputProps={{ placeholder: word("auth.form.placeholder.email") }}
      />

      {/* PHONE */}
      <PhoneNumberInput
        label={word("global.form.label.phoneNumber")}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        name="phoneNumberInput"
        error={getFormErrorMessage("phoneNumberInput")}
        countryCodeName="phoneNumberCountryCode"
        countryCode={formik.values.phoneNumberCountryCode as TCountryCode}
      />

      {/* HEAR ABOUT US */}
      <div className="flex w-full flex-col gap-2">
        <DropdownInput
          label={word("box.lead.form.input.hearAboutUs.label")}
          options={hearAboutUsItem}
          error={getFormErrorMessage("hearAboutUs")}
        >
          <Select
            value={formik.values.hearAboutUs ?? ""}
            onValueChange={(value) => {
              formik.setFieldValue("hearAboutUs", value)
            }}
            items={hearAboutUsItem}
          >
            <SelectTrigger>
              <SelectValue placeholder={word("box.lead.form.input.hearAboutUs.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {hearAboutUsItem.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DropdownInput>

        {formik.values.hearAboutUs === HeardAboutUs.OTHER && (
          <StandardInput
            setFieldValue={formik.setFieldValue}
            getFieldProps={formik.getFieldProps}
            name="hearAboutUsSpecify"
            error={getFormErrorMessage("hearAboutUsSpecify")}
            inputProps={{ placeholder: word("box.lead.form.input.hearAboutUs.otherPlaceholder") }}
          />
        )}
      </div>
      {/* EXPERIENCE LEVEL */}
      <DropdownInput
        label={word("box.lead.form.input.experienceLevel.label")}
        options={experienceLevelItem}
        error={getFormErrorMessage("experienceLevel")}
      >
        <Select
          value={formik.values.experienceLevel ?? ""}
          onValueChange={(value) => {
            formik.setFieldValue("experienceLevel", value)
          }}
          items={experienceLevelItem}
        >
          <SelectTrigger id={"experienceLevel"}>
            <SelectValue placeholder={word("box.lead.form.input.experienceLevel.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {experienceLevelItem.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DropdownInput>

      {/* GOALS */}
      <InputContainer label={word("box.lead.form.input.goals.label")} error={getFormErrorMessage("goals")}>
        <ChipGroup
          multiple  
          className="flex-wrap gap-2"
          value={formik.values.goals ?? []}
          onValueChange={(value) => {
            void formik.setFieldValue("goals", value)
          }}
        >
          {goalsItem.map((goal) => (
            <ChipGroupItem key={goal.value} value={goal.value}>
              <Text fontStyle="LM" className="flex items-center text-textDefault-body">
                {goal.label}
              </Text>
            </ChipGroupItem>
          ))}
        </ChipGroup>
      </InputContainer>

      {/* BEST TIME TO CALL */}
      <TextAreaInput
        label={word("box.lead.form.input.bestTimeToContact.label")}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        name="bestTimeToContact"
        error={getFormErrorMessage("bestTimeToContact")}
        textareaProps={{
          className: "field-sizing-fixed",
          placeholder: word("box.lead.form.input.bestTimeToContact.placeholder"),
          rows: 5,
        }}
        isRequiredInput={false}
      />
      {/* INTERNAL NOTE */}
      <div className="flex flex-col gap-2">
        <Text fontStyle="BL" className="text-textDefault-body">
          {word("global.form.label.internalNote")}
        </Text>
        <div className="flex flex-row items-center justify-center gap-2">
          <WarningIcon className="text-textDefault-body" width={30} height={30} color="currentColor" />
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("box.lead.form.input.internalNote.warning")}
          </Text>
        </div>
        <TextAreaInput
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          name="note"
          error={getFormErrorMessage("note")}
          textareaProps={{
            placeholder: word("box.lead.form.input.internalNote.placeholder"),
            className: "field-sizing-fixed",
            rows: 5,
          }}
          isRequiredInput={false}
        />
      </div>
      {/* BUTTONS */}
      <div className="flex w-full flex-row items-center justify-center gap-2">
        <Button variant="outline" size="lg" className="grow" onClick={onPrevious}>
          {word("global.previous")}
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="grow"
          onClick={handlePreSubmit}
          isLoading={isSubmitting}
          disabled={disabled || isSubmitting}
        >
          {isSubmitting && <LoaderCircle className="animate-spin" />}
          {isEdition ? word("global.edit") : word("global.create")}
        </Button>
      </div>
    </div>
  )
}
